import {
  AfterViewInit,
  Component,
  HostListener,
  OnDestroy,
} from '@angular/core';
import {
  NavigationCancel,
  NavigationEnd,
  NavigationStart,
  Router,
} from '@angular/router';
import { RoutingHistoryService } from './service/routing-history/routing-history.service';
import { LoggingService } from './service/logging-service/logging.service';
import { MatDialog } from '@angular/material/dialog';
import { ErrorDialogComponent } from './component/dialog/error/error-dialog.component';
import { routeNames } from '@assets/val/route-constants';
import { Market } from './model/payment-request/payment-request.dto';
import { IframeService } from './service/iframe/iframe.service';
import { QueryParamsService } from './service/query-params/query-params.service';
import { ColorService } from './service/color/color.service';
import { RecordingService } from './service/recording-service/recording.service';
import { LoaderMessagingService } from './service/loader-messaging/loader-messaging.service';
import { StateSyncService } from './service/state-sync/state-sync.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.css'],
})
export class AppComponent implements AfterViewInit, OnDestroy {
  public navigating: boolean;
  public inIframe: boolean;

  loaderEvent = this.loaderMessagingService.handleMessage.bind(
    this.loaderMessagingService
  );

  constructor(
    private router: Router,
    private routingHistoryService: RoutingHistoryService,
    private loggingService: LoggingService,
    private dialogService: MatDialog,
    private iframeService: IframeService,
    private queryParamsService: QueryParamsService,
    private colorService: ColorService,
    private recordingService: RecordingService,
    private loaderMessagingService: LoaderMessagingService,
    private stateSyncService: StateSyncService
  ) {
    this.navigating = true;
    this.inIframe = this.iframeService.isThisInIframe();
    const params = this.queryParamsService.getQueryParams();
    this.colorService.setDynamicStyles(params);
    this.iframeService.setWindowMode(params);
    this.iframeService.initIframeApp(params);
    this.routingHistoryService.loadRouting();
    this.loggingService.renewContextId();
    this.recordingService.loadRecordingService();
    this.stateSyncService.initialize();
  }

  shouldDisplayLocaleSelector: boolean = false;
  shouldDisplayDisclaimer: boolean = false;
  shouldDisplayHeader: boolean = true;

  ngAfterViewInit() {
    if (this.inIframe) {
      this.shouldDisplayHeader = false;
      window.addEventListener('message', (e) => {
        this.loaderMessagingService.handleMessage(e);
      });
    }

    if (!this.storageAvailable('localStorage')) {
      const dialogConfiguration = {
        maxHeight: '50vh',
        maxWidth: '80vw',
        data: {
          message:
            'Det verkar som din enhet är i privat läge - gå ur privat läge för att genomföra betalningen',
          buttons: [
            {
              title: 'ok',
              callback: () => {
                this.router.navigate([routeNames.ERROR], {
                  queryParams: {
                    fatal: 'true',
                    error: 'Gå ur privat läge för att genomföra betalningen',
                  },
                  queryParamsHandling: 'preserve',
                });
              },
            },
          ],
        },
      };
      this.loggingService.reportCrash('localstorage unavailable');
      this.dialogService.open(ErrorDialogComponent, dialogConfiguration);
    } else {
      this.loggingService.setPersistentId();
    }

    this.router.events.subscribe((event) => {
      if (event instanceof NavigationStart) {
        this.navigating = true;
      } else if (
        event instanceof NavigationEnd ||
        event instanceof NavigationCancel
      ) {
        this.navigating = false;

        const isSeMarket: boolean = window.location.href.includes('/se?');
        this.shouldDisplayHeader = !(
          this.router.isActive(routeNames.LOAN_CALCULATOR, false) ||
          this.inIframe
        );

        // Displaying disclaimer under footer only for matching pages:
        this.shouldDisplayDisclaimer =
          this.router.isActive(routeNames.INVOICE_CONFIRM, false) ||
          this.router.isActive(routeNames.SELECT_INSTALLMENT_PLAN, false) ||
          (isSeMarket &&
            this.router.isActive(routeNames.LOAN_CALCULATOR, false));

        this.shouldDisplayLocaleSelector = this.getIsMarketInRoute(Market.FI);
      }
    });

    this.startRecording();
  }

  ngOnDestroy(): void {
    window.removeEventListener('message', this.loaderEvent);
  }

  @HostListener('window:unload', ['$event'])
  unloadHandler(event: Event): void {
    // Perform cleanup before app is closed.
    this.iframeService.messageDestroyed();
  }

  //https://developer.mozilla.org/en-US/docs/Web/API/Web_Storage_API/Using_the_Web_Storage_API
  storageAvailable(typeKey: string) {
    var storage;
    try {
      storage = window[typeKey];
      var x = '__storage_test__';
      storage.setItem(x, x);
      storage.removeItem(x);
      return true;
    } catch (e) {
      return (
        e instanceof DOMException &&
        // everything except Firefox
        (e.code === 22 ||
          // Firefox
          e.code === 1014 ||
          // test name field too, because code might not be present
          // everything except Firefox
          e.name === 'QuotaExceededError' ||
          // Firefox
          e.name === 'NS_ERROR_DOM_QUOTA_REACHED') &&
        // acknowledge QuotaExceededError only if there's something already stored
        storage &&
        storage.length !== 0
      );
    }
  }

  getIsMarketInRoute(market: Market): boolean {
    return (
      this.router.url.includes(`/${market.toLowerCase()}?`) ||
      this.router.url.includes(`/${market.toLowerCase()}/`)
    );
  }

  startRecording(): void {
    this.recordingService.getRecordingStatus().subscribe((status: boolean) => {
      if (status) {
        this.recordingService.startRecording();
      }
    });
  }
}
